'use client';

import { useState, useActionState, useEffect } from 'react';
import MagazineComponent from '@pixum/magazine';
import { MAGAZINE_FOCUS } from 'data-layer/constants';
import { EMAIL_VALIDATION_ERROR } from 'utils';
import { ensureSession } from 'services/session/client';
import { submitDownloadSoftwareFormAction, type DownloadSoftwareFormState } from '../actions';
import type { DownloadVoucherCampaignProps } from '../types';
import { CampaignHeader } from './CampaignHeader';
import { EmailCaptureForm } from './EmailCaptureForm';
import { SuccessMessage } from './SuccessMessage';
import { MagazinePicture } from '../../../magazineCollection/variants/partials/MagazinePicture';
import '../styles.css';

const SESSION_ERROR_MESSAGE = 'Session required. Please reload the page.';
const SESSION_REQUIRED_ERROR_CODE = 'SESSION_REQUIRED';

/**
 * DownloadVoucherCampaign - Magazine-style layout variant for software download voucher campaign.
 *
 * Uses Magazine component for two-column layout:
 * - Left: Voucher image
 * - Right: Campaign header + email capture form
 *
 * Displays voucher information and collects email for voucher delivery.
 * Handles email submission with optional newsletter opt-in.
 * Uses Server Actions for form submission (sendEmailAction, setNewsletterAction).
 *
 * @example
 * ```tsx
 * <DownloadVoucherCampaign
 *   voucher={{
 *     headline: 'Gutschein für dein erstes Fotobuch',
 *     description: 'Für dein erstes Pixum Fotobuch...',
 *     code: 'PIXUM-2024',
 *     voucherImage: { ... }
 *   }}
 *   constants={{
 *     emailLabel: 'E-Mail Adresse',
 *     newsletterLabel: 'Ich möchte den Newsletter erhalten',
 *     submitButtonHowToInstall: 'GUTSCHEIN PER E-MAIL ERHALTEN',
 *   }}
 *   emailId={215}
 *   device="desktop"
 * />
 * ```
 */
export function DownloadVoucherCampaign({
  voucher,
  constants,
  variant,
  emailId,
  onSuccess,
  device,
  openInDialog,
  onInstallClick
}: DownloadVoucherCampaignProps) {
  // Local state for controlled inputs
  const [email, setEmail] = useState('');
  const [newsletterChecked, setNewsletterChecked] = useState(false);
  const [clearError, setClearError] = useState(false);

  /**
   * Wrapper for useActionState.
   * Uses controlled state (email, newsletterChecked).
   */
  const submitForm = async (): Promise<DownloadSoftwareFormState> => {
    try {
      // A fast submit waits for the client session: withSecureServerAction rejects calls without a JWT cookie.
      await ensureSession();
    } catch {
      return { success: false, error: SESSION_ERROR_MESSAGE, errorCode: SESSION_REQUIRED_ERROR_CODE };
    }

    return submitDownloadSoftwareFormAction({
      email,
      newsletter: newsletterChecked,
      emailId
    });
  };

  // useActionState for Server Action integration
  const [state, formAction, isPending] = useActionState(submitForm, { success: false });

  // Handle side effects
  useEffect(() => {
    if (isPending) {
      setClearError(false);
    } else if (state.success && onSuccess) {
      onSuccess();
    }
  }, [isPending, state.success, onSuccess]);

  // Derive error state from Server Action state
  const hasError = !isPending && !!state.error && !clearError;

  /**
   * Map error codes to localized Contentful constants.
   * Falls back to server error message if no localized version available.
   */
  const errorMessage = (() => {
    if (!state.error || !state.errorCode) return undefined;

    if (state.errorCode === EMAIL_VALIDATION_ERROR.EMPTY && constants.emailEmpty) {
      return constants.emailEmpty;
    }

    if (state.errorCode === EMAIL_VALIDATION_ERROR.INVALID_FORMAT && constants.emailInvalid) {
      return constants.emailInvalid;
    }

    if (state.errorCode === SESSION_REQUIRED_ERROR_CODE && constants.sessionRequired) {
      return constants.sessionRequired;
    }

    return state.error;
  })();

  /**
   * Clears error display when user focuses on email input.
   * Provides immediate UX feedback (error hidden until next submission).
   */
  const handleFocus = () => {
    setClearError(true);
  };

  /**
   * Toggles newsletter checkbox.
   */
  const handleCheckboxChange = () => {
    setNewsletterChecked(!newsletterChecked);
  };

  /**
   * Updates email state.
   */
  const handleEmailChange = (value: string) => {
    setEmail(value);
  };

  // Media (left side): Voucher image
  const media = voucher.voucherImage && <MagazinePicture image={voucher.voucherImage} device={device} />;

  // Text (right side): Campaign header + form or success message
  const text = (
    <div className="download-voucher-campaign__content">
      {state.success ? (
        <SuccessMessage constants={constants} openInDialog={openInDialog} onInstallClick={onInstallClick} />
      ) : (
        <>
          <CampaignHeader voucher={voucher} variant={variant} openInDialog={openInDialog} constants={constants} />

          <EmailCaptureForm
            constants={constants}
            email={email}
            hasError={hasError}
            errorMessage={hasError ? errorMessage : undefined}
            isPending={isPending}
            newsletterChecked={newsletterChecked}
            onEmailChange={handleEmailChange}
            onFocus={handleFocus}
            onNewsletterChange={handleCheckboxChange}
            action={formAction}
          />
        </>
      )}
    </div>
  );

  const magazineProps = {
    className: 'download-voucher-campaign',
    focus: MAGAZINE_FOCUS.TEXT,
    level: 3 as const,
    media,
    text
  };

  return <MagazineComponent {...magazineProps} />;
}
